import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC } from 'react'
import { useNewApplicationFormStore } from '../../../../stores/applications/newApplicationFormStore'
import ApproverOverview from '../../../ControlledFormInputs/ApproverOverview'
import { getApplicationFundingApprovals } from '../../../../helper/applicationTagApproval'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'

const getDisplayText = (value: any) => {
	if (Array.isArray(value)) {
		return value.filter(Boolean).join(', ')
	}

	if (value && typeof value === 'object') {
		return value.label || value.value || ''
	}

	return value || ''
}

const getDisplayValue = (value: any) => getDisplayText(value) || '-'

type ReviewField = {
	label: string
	value: any
}

const ApplicationReview: FC = () => {
	const {
		formValues,
		actions: { setCurrentStep }
	} = useNewApplicationFormStore()
	const { formValues: tagFormValues } = useGenericTagsManagementFormStore()


	const applicationTargetFields: Array<ReviewField> = [
		{
			label: 'GCP Project',
			value: formValues.gcpProjectID
		},
		{
			label: 'Infrastructure Type',
			value: formValues.infrastructureType === 'gke' ? "GKE" : "Non-GKE"
		},
		...(formValues.infrastructureType === 'gke'
			? [
					{
						label: 'GKE Workload Cluster',
						value: formValues.gcpProjectID?.resolution?.resolvedCluster?.namePrefix
					}
				]
			: [])
	]

	const applicationDetailsFields: Array<ReviewField> = [
		{ label: 'Application Name', value: formValues.applicationName },
		{ label: 'Application Alias', value: formValues.applicationAlias },
		{
			label: 'Application Viewer(s)',
			value: formValues.applicationViewer
		},
		{
			label: 'Platform Owner',
			value: formValues.platformOwner
		},
		{
			label: 'Platform Owner Email',
			value: formValues.platformOwnerEmail
		},
		{
			label: 'Application Owner Name',
			value: formValues.applicationOwner
		},
		{
			label: 'Application Owner Email',
			value: formValues.applicationOwnerEmail
		},
		{
			label: 'Deployment',
			value: String(formValues.deployment || '').toUpperCase()
		},
		{
			label: 'Application Data Classification',
			value: formValues.dataClassification
		},
		{ label: 'Tier Level', value: formValues.tierLevel },
		{ label: 'Portfolio', value: formValues.portfolio }
	]

	const applicationApprovalPreview = getApplicationFundingApprovals(
		formValues.applicationName
	)
	const resourceTagFields: Array<ReviewField> = [
		{ label: 'Group', value: tagFormValues.group },
		{ label: 'Funding Source', value: tagFormValues.fundingSourceType },
		{ label: 'Data Classification', value: tagFormValues.dataClassification },
		{ label: 'WBS Code', value: tagFormValues.WBSCode },
		{ label: 'Cost Center', value: tagFormValues.costCenter }
	]

	const renderReviewSection = (
		title: string,
		stepIndex: number,
		items: Array<{ label: string; value: any }>
	) => (

		// Template
		<Container
			header={
				<Header
					variant="h3"
					actions={
						<Button onClick={() => setCurrentStep(stepIndex)}>Edit</Button>
					}
				>
					{title}
				</Header>
			}
		>
			<ColumnLayout columns={2} variant="text-grid">
				{items.map((item) => (
					<div key={item.label}>
						<Box variant="awsui-key-label">{item.label}</Box>
						<div>{getDisplayValue(item.value)}</div>
					</div>
				))}
			</ColumnLayout>
		</Container>
	)

	return (
		<SpaceBetween direction="vertical" size="l">
			{renderReviewSection('Application Target', 0, applicationTargetFields)}

			{renderReviewSection('Add-ons', 0, [
				{
					label: 'MongoDB',
					value: formValues.addOns?.mongodb === true ? 'Yes' : 'No'
				},
				{
					label: 'Confluent',
					value: formValues.addOns?.confluent === true ? 'Yes' : 'No'
				}
			])}

			{renderReviewSection('Application Details', 0, applicationDetailsFields)}

			<Container
				header={
					<Header
						variant="h3"
						actions={
							<Button onClick={() => setCurrentStep(1)}>Edit</Button>
						}
					>
						Resource Tags
					</Header>
				}
			>
				<SpaceBetween direction="vertical" size="m">
					<ColumnLayout columns={2} variant="text-grid">
						{resourceTagFields.map((item) => (
							<div key={item.label}>
								<Box variant="awsui-key-label">{item.label}</Box>
								<div>{getDisplayValue(item.value)}</div>
							</div>
						))}
					</ColumnLayout>
					<div>
						<Box variant="awsui-key-label">Application Owner</Box>
						{formValues.applicationOwner || '-'}
					</div>
					<ApproverOverview
						approvalStatus={applicationApprovalPreview}
						isDisabled
					/>
				</SpaceBetween>
			</Container>
		</SpaceBetween>
	)
}

export default ApplicationReview
